import {
	type QueryClient,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthProvider";
import { authKeys } from "@/services/auth/auth.options";
import { userConfigKeys, userConfigService } from "@/services/user-config";
import type { ConfigurationValue } from "@/services/user-config/user-config.types";
import { userKeys } from "../user";
import { organizationKeys } from "./organization.options";
import { clearSavedSubContractorFilters } from "./remove-deleted-sub-contractor-filters";
import { clearSavedSubOrganizationFilters } from "./remove-deleted-sub-organization-filters";
import { organizationService } from "./organization.service";
import { OrganizationStatusCode } from "./organization.types";
import type {
	CheckEmailUsage,
	CreateOrganizationInfo,
	CreateOrganizationUserInfo,
	CreateSubContractorInfo,
	CreateSubOrganizationInfo,
	DataAccessInfo,
	OAuthProvider,
	Organization,
	OrganizationParams,
	OrganizationRoleInfo,
	ServiceResult,
	UpdateOrganizationContactInfo,
	UpdateOrganizationInfo,
	UpdateOrganizationStatus,
	UpdateOrganizationUserInfo,
	UpdateOrganizationUserStatus,
	UpdateSubContractorInfo,
	UpdateSubOrganizationContactInfo,
} from "./organization.types";

function invalidateRoleUserCounts(
	queryClient: QueryClient,
	organizationId: string,
) {
	queryClient.invalidateQueries({
		queryKey: organizationKeys.organizationRoles(organizationId),
	});
	queryClient.invalidateQueries({
		queryKey: organizationKeys.globalRoles(),
	});
}

// ==================== CORE ORGANIZATION MUTATIONS ====================

/**
 * Create a new organization with enhanced error handling and optimistic updates
 */
export const useCreateOrganization = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateOrganizationInfo) =>
			organizationService.createOrganization(data),
		onMutate: async (newOrganization) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({
				queryKey: organizationKeys.organizations(),
			});

			// Snapshot the previous value
			const previousOrganizations = queryClient.getQueriesData({
				queryKey: organizationKeys.organizations(),
			});

			// Optimistically update the cache (only organization list queries have { data: [] };
			// do not mutate statuses or other sub-queries that share the same key prefix)
			queryClient.setQueriesData(
				{ queryKey: organizationKeys.organizations() },
				(old: unknown) => {
					if (!old || typeof old !== "object") return old;
					const oldData = old as { data?: unknown[] };
					if (!Array.isArray(oldData.data)) return old;
					return {
						...oldData,
						data: [
							...(oldData.data || []),
							{
								id: `temp-${Date.now()}`,
								statusCode: 1,
								active: false,
								createdAt: new Date().toISOString(),
								...newOrganization,
							},
						],
					};
				},
			);

			return { previousOrganizations };
		},
		onSuccess: (result: ServiceResult) => {
			// Invalidate organizations list queries
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizations(),
			});

			// If we have the affected ID, we can be more specific
			if (result.affectedId) {
				// The new organization should be refetched when accessed
				queryClient.invalidateQueries({
					queryKey: organizationKeys.organizationsDetail(result.affectedId),
				});
			}
			return result;
		},
		onError: (error, _newOrganization, context) => {
			// Revert optimistic update on error
			if (context?.previousOrganizations) {
				context.previousOrganizations.forEach(([queryKey, data]) => {
					queryClient.setQueryData(queryKey, data);
				});
			}

			// Enhanced error handling with user-friendly messages
			console.error("Failed to create organization:", error);
			// You could also show a toast notification here
			// toast.error(`Failed to create organization: ${error.message}`);
		},
		onSettled: () => {
			// Always refetch after error or success
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizations(),
			});
		},
	});
};

/**
 * Update an existing organization
 */
export const useUpdateOrganization = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationInfo }) =>
			organizationService.updateOrganization(id, data),
		onMutate: async ({ id, data }) => {
			await queryClient.cancelQueries({
				queryKey: organizationKeys.organizationsDetail(id),
			});
			const prev = queryClient.getQueryData<Organization>(
				organizationKeys.organizationsDetail(id),
			);

			// optimistic
			if (prev) {
				queryClient.setQueryData(organizationKeys.organizationsDetail(id), {
					...prev,
					...data,
					updatedAt: new Date().toISOString(),
				} as Organization);
			}

			return { prev, id };
		},
		onError: (_err, { id }, ctx) => {
			if (ctx?.prev) {
				queryClient.setQueryData(
					organizationKeys.organizationsDetail(id),
					ctx.prev,
				);
			}
		},
		onSuccess: (_result, { id }) => {
			// mark stale + background refetch (active observers)
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationsDetail(id),
			});
			// also list if name might appear in lists:
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizations(),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationsHistory(id),
			});
		},
	});
};

const ORGANIZATION_STATUS_BY_API_VALUE: Record<
	string,
	{ code: OrganizationStatusCode; name: string }
> = {
	active: { code: OrganizationStatusCode.Active, name: "Active" },
	suspended: { code: OrganizationStatusCode.Suspended, name: "Suspended" },
	archived: { code: OrganizationStatusCode.Archived, name: "Archived" },
	retired: { code: OrganizationStatusCode.Retired, name: "Retired" },
};

function organizationStatusFromApiValue(status: string | null | undefined) {
	if (!status) return undefined;
	return ORGANIZATION_STATUS_BY_API_VALUE[status];
}

/**
 * Update organization status
 */
export const useUpdateOrganizationStatus = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: string;
			data: UpdateOrganizationStatus;
		}) => organizationService.updateOrganizationStatus(id, data),
		onMutate: async ({ id, data }) => {
			await queryClient.cancelQueries({
				queryKey: organizationKeys.organizationsDetail(id),
			});
			const prev = queryClient.getQueryData<Organization>(
				organizationKeys.organizationsDetail(id),
			);
			const nextStatus = organizationStatusFromApiValue(data.status);
			if (prev && nextStatus) {
				queryClient.setQueryData(organizationKeys.organizationsDetail(id), {
					...prev,
					status: {
						...(prev.status ?? {}),
						name: nextStatus.name,
						code: nextStatus.code,
						description: prev.status?.description ?? null,
					},
					updatedAt: new Date().toISOString(),
				} as Organization);
			}
			return { prev, id };
		},
		onError: (_err, { id }, ctx) => {
			if (ctx?.prev) {
				queryClient.setQueryData(
					organizationKeys.organizationsDetail(id),
					ctx.prev,
				);
			}
		},
		onSuccess: (_result, { id }) => {
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationsDetail(id),
				refetchType: "all",
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizations(),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationsHistory(id),
			});
			queryClient.invalidateQueries({
				queryKey: authKeys.checkActionRoot(),
				refetchType: "all",
			});
			queryClient.invalidateQueries({
				queryKey: authKeys.checkActionsRoot(),
				refetchType: "all",
			});
		},
	});
};

// ==================== ORGANIZATION ROLES MUTATIONS ====================

/**
 * Create a new organization role
 */
export const useCreateOrganizationRole = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			data,
		}: {
			organizationId: string;
			data: OrganizationRoleInfo;
		}) => organizationService.createOrganizationRole(organizationId, data),
		onSuccess: (_result, variables) => {
			const { organizationId } = variables;

			// Invalidate organization roles
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationRoles(organizationId),
			});
		},
		onError: (error) => {
			console.error("Failed to create organization role:", error);
		},
	});
};

/**
 * Update an organization role
 */
export const useUpdateOrganizationRole = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			roleId,
			data,
		}: {
			organizationId: string;
			roleId: string;
			data: OrganizationRoleInfo;
		}) =>
			organizationService.updateOrganizationRole(organizationId, roleId, data),
		onSuccess: (_result, variables) => {
			const { organizationId, roleId } = variables;

			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationRoles(organizationId),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.allOrganizationRolesDetail(roleId),
			});
		},
		onError: (error) => {
			console.error("Failed to update organization role:", error);
		},
	});
};

/**
 * Delete an organization role
 */
export const useDeleteOrganizationRole = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			roleId,
		}: {
			organizationId: string;
			roleId: string;
		}) => organizationService.deleteOrganizationRole(organizationId, roleId),
		onSuccess: (_result, variables) => {
			const { organizationId } = variables;

			// Invalidate organization roles
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationRoles(organizationId),
			});
		},
		onError: (error) => {
			console.error("Failed to delete organization role:", error);
		},
	});
};

// ==================== ORGANIZATION CONTACTS MUTATIONS ====================

/**
 * Update an organization contact
 */
export const useUpdateOrganizationContact = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			contactId,
			data,
		}: {
			organizationId: string;
			contactId: string;
			data: UpdateOrganizationContactInfo;
		}) =>
			organizationService.updateOrganizationContact(
				organizationId,
				contactId,
				data,
			),
		onSuccess: (_result, variables) => {
			const { organizationId } = variables;

			// Invalidate organization contacts
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationContacts(organizationId),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationsHistory(organizationId),
			});
		},
		onError: (error) => {
			console.error("Failed to update organization contact:", error);
		},
	});
};

// ==================== ORGANIZATION OAUTH MUTATIONS ====================

/**
 * Update organization OAuth provider information
 */
export const useUpdateOrganizationOAuthInfo = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			data,
		}: {
			organizationId: string;
			data: OAuthProvider;
		}) => organizationService.updateOrganizationOAuthInfo(organizationId, data),
		onSuccess: (_result, variables) => {
			const { organizationId } = variables;

			// Invalidate organization OAuth info
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationOAuth(organizationId),
			});

			// Also invalidate the organization detail since it includes OAuth provider
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationsDetail(organizationId),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationsHistory(organizationId),
			});
		},
		onError: (error) => {
			console.error("Failed to update organization OAuth info:", error);
		},
	});
};

// ==================== ORGANIZATION USERS MUTATIONS ====================

/**
 * Add a user to an organization
 */
export const useAddOrganizationUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			data,
		}: {
			organizationId: string;
			data: CreateOrganizationUserInfo;
		}) => organizationService.addOrganizationUser(organizationId, data),
		onSuccess: (_result, variables) => {
			const { organizationId } = variables;

			// Invalidate organization users queries
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsers(organizationId),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsersCount(organizationId),
			});
			if (variables.data.subContractorId) {
				queryClient.invalidateQueries({
					queryKey: organizationKeys.subContractors(organizationId),
				});
			}
			invalidateRoleUserCounts(queryClient, organizationId);
		},
		onError: (error) => {
			console.error("Failed to add organization user:", error);
		},
	});
};

/**
 * Update an organization user
 */
/**
 * Update an organization user with optimistic updates
 */
export const useUpdateOrganizationUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			organizationUserId,
			data,
		}: {
			organizationId: string;
			organizationUserId: string;
			data: UpdateOrganizationUserInfo;
		}) =>
			organizationService.updateOrganizationUser(
				organizationId,
				organizationUserId,
				data,
			),
		onMutate: async ({ organizationId, organizationUserId, data }) => {
			// Cancel any outgoing refetches for the user detail query
			await queryClient.cancelQueries({
				queryKey: organizationKeys.organizationUsersDetail(
					organizationId,
					organizationUserId,
				),
			});

			// Snapshot the previous value from organization user detail
			const previousOrgUser = queryClient.getQueryData(
				organizationKeys.organizationUsersDetail(
					organizationId,
					organizationUserId,
				),
			);

			// Optimistically update the organization user detail
			if (previousOrgUser) {
				queryClient.setQueryData(
					organizationKeys.organizationUsersDetail(
						organizationId,
						organizationUserId,
					),
					{
						...previousOrgUser,
						...data,
						updatedAt: new Date().toISOString(),
					},
				);
			}

			// Return context with previous values for rollback
			return { previousOrgUser, organizationId, organizationUserId };
		},
		onSuccess: (result, variables) => {
			const { organizationId, organizationUserId } = variables;

			// Invalidate the user detail if we have the affectedId
			if (result.affectedId) {
				queryClient.invalidateQueries({
					queryKey: userKeys.usersDetail(result.affectedId),
				});
				queryClient.invalidateQueries({
					queryKey: userKeys.usersHistory(result.affectedId),
				});
			}

			// Invalidate the organization user detail to refetch fresh data
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsersDetail(
					organizationId,
					organizationUserId,
				),
			});

			// Invalidate organization users list
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsers(organizationId),
			});

			// Belongs To changes the stored user count on each subcontractor row.
			if ("subContractorId" in variables.data) {
				queryClient.invalidateQueries({
					queryKey: organizationKeys.subContractors(organizationId),
				});
			}
			if ("roleId" in variables.data) {
				invalidateRoleUserCounts(queryClient, organizationId);
			}
		},
		onError: (_error, _variables, context) => {
			// Rollback optimistic update if mutation fails
			if (context?.previousOrgUser) {
				queryClient.setQueryData(
					organizationKeys.organizationUsersDetail(
						context.organizationId,
						context.organizationUserId,
					),
					context.previousOrgUser,
				);
			}
			console.error("Failed to update organization user:", _error);
		},
		onSettled: (_data, _error, variables) => {
			const { organizationId, organizationUserId } = variables;

			// Always refetch after error or success to ensure data consistency
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsersDetail(
					organizationId,
					organizationUserId,
				),
			});
		},
	});
};

/**
 * Update organization user status
 */
export const useUpdateOrganizationUserStatus = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			organizationUserId,
			data,
		}: {
			organizationId: string;
			organizationUserId: string; // Updated from userId per swagger2.json
			data: UpdateOrganizationUserStatus;
		}) =>
			organizationService.updateOrganizationUserStatus(
				organizationId,
				organizationUserId,
				data,
			),
		onSuccess: (_result, variables) => {
			const { organizationId, organizationUserId } = variables;

			// Invalidate the specific user
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsersDetail(
					organizationId,
					organizationUserId,
				),
			});

			// Invalidate organization users list queries (status might affect filtering)
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsers(organizationId),
			});
			invalidateRoleUserCounts(queryClient, organizationId);

			// Broad invalidation covers all user history queries (no affectedId available)
			queryClient.invalidateQueries({
				queryKey: userKeys.users(),
			});
			queryClient.invalidateQueries({
				queryKey: authKeys.checkActionRoot(),
				refetchType: "all",
			});
			queryClient.invalidateQueries({
				queryKey: authKeys.checkActionsRoot(),
				refetchType: "all",
			});
		},
		onError: (error) => {
			console.error("Failed to update organization user status:", error);
		},
	});
};

/**
 * Force password reset for an organization user (org-admin)
 * PUT /organizations/{organizationId}/users/{organizationUserId}/password-reset
 */
export const useForceOrganizationUserPasswordReset = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			organizationUserId,
		}: {
			organizationId: string;
			organizationUserId: string;
		}) =>
			organizationService.forceOrganizationUserPasswordReset(
				organizationId,
				organizationUserId,
			),
		onSuccess: (_result, { organizationId, organizationUserId }) => {
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsersDetail(
					organizationId,
					organizationUserId,
				),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsers(organizationId),
			});
			// Broad invalidation covers all user history queries (no affectedId available)
			queryClient.invalidateQueries({
				queryKey: userKeys.users(),
			});
		},
		onError: (error) => {
			console.error("Failed to force organization user password reset:", error);
		},
	});
};

// ==================== SUB-ORGANIZATIONS MUTATIONS ====================

/**
 * Create a new sub-organization
 * POST /organizations/{organizationId}/sub-organizations
 */
export const useCreateSubOrganization = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			data,
		}: {
			organizationId: string;
			data: CreateSubOrganizationInfo;
		}) => organizationService.createSubOrganization(organizationId, data),
		onSuccess: (_result, variables) => {
			const { organizationId } = variables;

			// Invalidate sub-organizations list and count queries
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subOrganizations(organizationId),
			});
		},
		onError: (error) => {
			console.error("Failed to create sub-organization:", error);
		},
	});
};

/**
 * Update an existing sub-organization
 * PUT /organizations/{organizationId}/sub-organizations/{subOrganizationId}
 */
export const useUpdateSubOrganization = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			subOrganizationId,
			data,
		}: {
			organizationId: string;
			subOrganizationId: string;
			data: UpdateSubOrganizationContactInfo;
		}) =>
			organizationService.updateSubOrganization(
				organizationId,
				subOrganizationId,
				data,
			),
		onSuccess: (_result, variables) => {
			const { organizationId, subOrganizationId } = variables;

			// Invalidate sub-organizations list and count queries
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subOrganizations(organizationId),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subOrganizationDetail(
					organizationId,
					subOrganizationId,
				),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subOrganizationsHistory(
					organizationId,
					subOrganizationId,
				),
			});
		},
		onError: (error) => {
			console.error("Failed to update sub-organization:", error);
		},
	});
};

function invalidateSubOrganizationStatus(
	queryClient: ReturnType<typeof useQueryClient>,
	organizationId: string,
	subOrganizationId: string,
) {
	queryClient.invalidateQueries({
		queryKey: organizationKeys.subOrganizations(organizationId),
	});
	queryClient.invalidateQueries({
		queryKey: organizationKeys.subOrganizationDetail(
			organizationId,
			subOrganizationId,
		),
	});
	queryClient.invalidateQueries({
		queryKey: authKeys.checkActionRoot(),
	});
}

/** Detail and history queries for a subcontractor that no longer exists. */
function queryObservesDeletedSubContractor(
	queryKey: readonly unknown[],
	subContractorId: string,
): boolean {
	return queryKey.some(
		(segment, index) =>
			segment === subContractorId &&
			(queryKey[index - 1] === "detail" || queryKey[index - 1] === "history"),
	);
}

function invalidateDeletedSubContractor(
	queryClient: ReturnType<typeof useQueryClient>,
	organizationId: string,
	subContractorId: string,
) {
	queryClient.invalidateQueries({
		queryKey: organizationKeys.subContractors(organizationId),
		predicate: (query) =>
			!queryObservesDeletedSubContractor(query.queryKey, subContractorId),
	});
	queryClient.invalidateQueries({
		queryKey: organizationKeys.subContractorDetail(
			organizationId,
			subContractorId,
		),
		refetchType: "none",
	});
	queryClient.invalidateQueries({
		queryKey: organizationKeys.subContractorsHistory(
			organizationId,
			subContractorId,
		),
		refetchType: "none",
	});
	queryClient.invalidateQueries({
		queryKey: authKeys.checkActionRoot(),
	});
}

/** Detail and history queries for a sub-organization that no longer exists. */
function queryObservesDeletedSubOrganization(
	queryKey: readonly unknown[],
	subOrganizationId: string,
): boolean {
	return queryKey.some(
		(segment, index) =>
			segment === subOrganizationId &&
			(queryKey[index - 1] === "detail" || queryKey[index - 1] === "history"),
	);
}

function invalidateDeletedSubOrganization(
	queryClient: ReturnType<typeof useQueryClient>,
	organizationId: string,
	subOrganizationId: string,
) {
	queryClient.invalidateQueries({
		queryKey: organizationKeys.subOrganizations(organizationId),
		predicate: (query) =>
			!queryObservesDeletedSubOrganization(query.queryKey, subOrganizationId),
	});
	queryClient.invalidateQueries({
		queryKey: organizationKeys.subOrganizationDetail(
			organizationId,
			subOrganizationId,
		),
		refetchType: "none",
	});
	queryClient.invalidateQueries({
		queryKey: organizationKeys.subOrganizationsHistory(
			organizationId,
			subOrganizationId,
		),
		refetchType: "none",
	});
	queryClient.invalidateQueries({
		queryKey: authKeys.checkActionRoot(),
	});
}

/**
 * PUT .../sub-organizations/{id}/close
 */
export const useCloseSubOrganization = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			subOrganizationId,
		}: {
			organizationId: string;
			subOrganizationId: string;
		}) =>
			organizationService.closeSubOrganization(
				organizationId,
				subOrganizationId,
			),
		onSuccess: (_result, variables) => {
			invalidateSubOrganizationStatus(
				queryClient,
				variables.organizationId,
				variables.subOrganizationId,
			);
		},
	});
};

function invalidateSubContractorStatus(
	queryClient: ReturnType<typeof useQueryClient>,
	organizationId: string,
	subContractorId: string,
) {
	queryClient.invalidateQueries({
		queryKey: organizationKeys.subContractors(organizationId),
	});
	queryClient.invalidateQueries({
		queryKey: organizationKeys.subContractorDetail(
			organizationId,
			subContractorId,
		),
	});
	queryClient.invalidateQueries({
		queryKey: authKeys.checkActionRoot(),
	});
	queryClient.invalidateQueries({
		predicate: (query) =>
			Array.isArray(query.queryKey) &&
			query.queryKey.includes("sub-contractors"),
	});
}

/**
 * PUT .../sub-contractors/{id}/close
 */
export const useCloseSubContractor = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			subContractorId,
		}: {
			organizationId: string;
			subContractorId: string;
		}) =>
			organizationService.closeSubContractor(
				organizationId,
				subContractorId,
			),
		onSuccess: (_result, variables) => {
			invalidateSubContractorStatus(
				queryClient,
				variables.organizationId,
				variables.subContractorId,
			);
		},
	});
};

/**
 * PUT .../sub-contractors/{id}/activate
 */
export const useActivateSubContractor = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			subContractorId,
		}: {
			organizationId: string;
			subContractorId: string;
		}) =>
			organizationService.activateSubContractor(
				organizationId,
				subContractorId,
			),
		onSuccess: (_result, variables) => {
			invalidateSubContractorStatus(
				queryClient,
				variables.organizationId,
				variables.subContractorId,
			);
		},
	});
};

/**
 * PUT .../sub-organizations/{id}/activate
 */
export const useActivateSubOrganization = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			subOrganizationId,
		}: {
			organizationId: string;
			subOrganizationId: string;
		}) =>
			organizationService.activateSubOrganization(
				organizationId,
				subOrganizationId,
			),
		onSuccess: (_result, variables) => {
			invalidateSubOrganizationStatus(
				queryClient,
				variables.organizationId,
				variables.subOrganizationId,
			);
		},
	});
};

/**
 * DELETE .../sub-organizations/{id}
 */
export const useDeleteSubOrganization = () => {
	const queryClient = useQueryClient();
	const { user } = useAuth();

	return useMutation({
		mutationFn: async ({
			organizationId,
			subOrganizationId,
		}: {
			organizationId: string;
			subOrganizationId: string;
		}) => {
			await organizationService.deleteSubOrganization(
				organizationId,
				subOrganizationId,
			);

			const userId = user?.id;
			if (!userId) {
				return { filtersCleared: false as const };
			}

			let configs: ConfigurationValue[];
			try {
				// The values query stays fresh for five minutes, so a cached
				// snapshot can omit filters that still exist on the server.
				configs = await userConfigService.getConfigurationValues(userId);
			} catch (error) {
				console.error(
					"Failed to clear deleted sub-organization filters:",
					error,
				);
				return { filtersCleared: false as const, filterCleanupError: error };
			}

			const outcome = await clearSavedSubOrganizationFilters({
				userId,
				configs,
				subOrganizationId,
				setValue: (change) =>
					userConfigService.setConfigurationValue(
						userId,
						change.key,
						change.value,
					),
			});

			queryClient.setQueryData(
				userConfigKeys.values(userId),
				outcome.nextConfigs,
			);
			if (!outcome.filtersCleared) {
				console.error(
					"Failed to clear deleted sub-organization filters:",
					outcome.filterCleanupError,
				);
			} else if (outcome.updated) {
				void queryClient.invalidateQueries({
					queryKey: userConfigKeys.values(userId),
				});
			}

			return {
				filtersCleared: outcome.filtersCleared,
				filterCleanupError: outcome.filterCleanupError,
			};
		},
		onSuccess: (_result, variables) => {
			invalidateDeletedSubOrganization(
				queryClient,
				variables.organizationId,
				variables.subOrganizationId,
			);
		},
	});
};

/**
 * DELETE .../sub-contractors/{id}
 */
export const useDeleteSubContractor = () => {
	const queryClient = useQueryClient();
	const { user } = useAuth();

	return useMutation({
		mutationFn: async ({
			organizationId,
			subContractorId,
		}: {
			organizationId: string;
			subContractorId: string;
		}) => {
			await organizationService.deleteSubContractor(
				organizationId,
				subContractorId,
			);

			const userId = user?.id;
			if (!userId) {
				return { filtersCleared: false as const };
			}

			let configs: ConfigurationValue[];
			try {
				configs = await userConfigService.getConfigurationValues(userId);
			} catch (error) {
				console.error("Failed to clear deleted subcontractor filters:", error);
				return { filtersCleared: false as const, filterCleanupError: error };
			}

			const outcome = await clearSavedSubContractorFilters({
				userId,
				configs,
				subContractorId,
				setValue: (change) =>
					userConfigService.setConfigurationValue(
						userId,
						change.key,
						change.value,
					),
			});

			queryClient.setQueryData(
				userConfigKeys.values(userId),
				outcome.nextConfigs,
			);
			if (!outcome.filtersCleared) {
				console.error(
					"Failed to clear deleted subcontractor filters:",
					outcome.filterCleanupError,
				);
			} else if (outcome.updated) {
				void queryClient.invalidateQueries({
					queryKey: userConfigKeys.values(userId),
				});
			}

			return {
				filtersCleared: outcome.filtersCleared,
				filterCleanupError: outcome.filterCleanupError,
			};
		},
		onSuccess: (_result, variables) => {
			invalidateDeletedSubContractor(
				queryClient,
				variables.organizationId,
				variables.subContractorId,
			);
		},
	});
};

// ==================== BULK OPERATIONS ====================

/**
 * Bulk create organization users
 * POST /organizations/{organizationId}/users/bulk
 */
export const useBulkCreateOrganizationUsers = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			users,
		}: {
			organizationId: string;
			users: CreateOrganizationUserInfo[];
		}) =>
			organizationService.bulkCreateOrganizationUsers(organizationId, users),
		onSuccess: (_result, variables) => {
			const { organizationId } = variables;

			// Invalidate organization users list and count queries
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsers(organizationId),
			});
			invalidateRoleUserCounts(queryClient, organizationId);
		},
		onError: (error) => {
			console.error("Failed to bulk create organization users:", error);
		},
	});
};

// ==================== BATCH OPERATIONS ====================

/**
 * Batch update multiple organization users
 */
export const useBatchUpdateOrganizationUsers = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			organizationId,
			updates,
		}: {
			organizationId: string;
			updates: Array<{
				userId: string;
				data: UpdateOrganizationUserInfo;
			}>;
		}) => {
			// Execute all updates in parallel
			const results = await Promise.allSettled(
				updates.map(({ userId, data }) =>
					organizationService.updateOrganizationUser(
						organizationId,
						userId,
						data,
					),
				),
			);

			// Return results with success/failure information
			return results.map((result, index) => ({
				userId: updates[index].userId,
				success: result.status === "fulfilled",
				data: result.status === "fulfilled" ? result.value : undefined,
				error: result.status === "rejected" ? result.reason : undefined,
			}));
		},
		onSuccess: (_results, variables) => {
			const { organizationId } = variables;

			// Invalidate all organization users queries
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsers(organizationId),
			});
			if (variables.updates.some((update) => "roleId" in update.data)) {
				invalidateRoleUserCounts(queryClient, organizationId);
			}
		},
		onError: (error) => {
			console.error("Failed to batch update organization users:", error);
		},
	});
};

/**
 * Batch update organization user statuses
 */
export const useBatchUpdateOrganizationUserStatuses = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			organizationId,
			updates,
		}: {
			organizationId: string;
			updates: Array<{ userId: string; data: UpdateOrganizationUserStatus }>;
		}) => {
			// Execute all updates in parallel
			const results = await Promise.allSettled(
				updates.map(({ userId, data }) =>
					organizationService.updateOrganizationUserStatus(
						organizationId,
						userId,
						data,
					),
				),
			);

			// Return results with success/failure information
			return results.map((result, index) => ({
				userId: updates[index].userId,
				success: result.status === "fulfilled",
				error: result.status === "rejected" ? result.reason : undefined,
			}));
		},
		onSuccess: (_results, variables) => {
			const { organizationId } = variables;

			// Invalidate all organization users queries
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsers(organizationId),
			});
			invalidateRoleUserCounts(queryClient, organizationId);
		},
		onError: (error) => {
			console.error(
				"Failed to batch update organization user statuses:",
				error,
			);
		},
	});
};

// ==================== OPTIMISTIC UPDATES ====================

/**
 * Update organization with optimistic updates
 */
export const useOptimisticUpdateOrganization = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationInfo }) =>
			organizationService.updateOrganization(id, data),
		onMutate: async ({ id, data }) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({
				queryKey: organizationKeys.organizationsDetail(id),
			});

			// Snapshot the previous value
			const previousOrganization = queryClient.getQueryData<Organization>(
				organizationKeys.organizationsDetail(id),
			);

			// Optimistically update the organization
			if (previousOrganization) {
				queryClient.setQueryData(organizationKeys.organizationsDetail(id), {
					...previousOrganization,
					...data,
					updatedAt: new Date().toISOString(),
				});
			}

			// Return a context object with the snapshotted value
			return { previousOrganization };
		},
		onError: (_error, { id }, context) => {
			// If the mutation fails, use the context returned from onMutate to roll back
			if (context?.previousOrganization) {
				queryClient.setQueryData(
					organizationKeys.organizationsDetail(id),
					context.previousOrganization,
				);
			}
		},
		onSettled: (_data, _error, { id }) => {
			// Always refetch after error or success
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationsDetail(id),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationsHistory(id),
			});
		},
	});
};

// ==================== ORGANIZATION USER DATA ACCESS MUTATIONS ====================

/**
 * Update data access entries for an organization user
 * PUT /organizations/{organizationId}/users/{organizationUserId}/data-access
 */
export const useUpdateOrganizationUserDataAccess = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			organizationUserId,
			dataAccess,
		}: {
			organizationId: string;
			organizationUserId: string;
			dataAccess: DataAccessInfo[];
		}) =>
			organizationService.updateOrganizationUserDataAccess(
				organizationId,
				organizationUserId,
				dataAccess,
			),
		onSuccess: (_result, { organizationId, organizationUserId }) => {
			// Invalidate the data access queries for this user
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUserDataAccess(
					organizationId,
					organizationUserId,
				),
			});

			// Also invalidate the user detail since data access may affect it
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationUsersDetail(
					organizationId,
					organizationUserId,
				),
			});

			// Invalidate sub-organizations list (user counts may change)
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subOrganizations(organizationId),
			});

			// Invalidate org-level data access list
			queryClient.invalidateQueries({
				queryKey: organizationKeys.organizationDataAccess(organizationId),
			});

			// Broad invalidation covers all user history queries (no affectedId available)
			queryClient.invalidateQueries({
				queryKey: userKeys.users(),
			});
		},
		onError: (error, { organizationId, organizationUserId }) => {
			console.error(
				`Failed to update data access for user ${organizationUserId} in organization ${organizationId}:`,
				error,
			);
		},
	});
};

// ==================== UTILITY MUTATIONS ====================

/**
 * Invalidate all organization-related queries
 */
export const useInvalidateOrganizationQueries = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (organizationId?: string) => {
			if (organizationId) {
				// Invalidate queries for a specific organization
				await Promise.all([
					queryClient.invalidateQueries({
						queryKey: organizationKeys.organizationsDetail(organizationId),
					}),
					queryClient.invalidateQueries({
						queryKey: organizationKeys.organizationRoles(organizationId),
					}),
					queryClient.invalidateQueries({
						queryKey: organizationKeys.organizationContacts(organizationId),
					}),
					queryClient.invalidateQueries({
						queryKey: organizationKeys.organizationOAuth(organizationId),
					}),
					queryClient.invalidateQueries({
						queryKey: organizationKeys.organizationUsers(organizationId),
					}),
				]);
			} else {
				// Invalidate all organization queries
				await queryClient.invalidateQueries({
					queryKey: organizationKeys.all,
				});
			}
		},
	});
};

/**
 * Prefetch organization data
 */
export const usePrefetchOrganization = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (organizationId: string) => {
			// Prefetch organization and its related data
			await Promise.all([
				queryClient.prefetchQuery({
					queryKey: organizationKeys.organizationsDetail(organizationId),
					queryFn: () => organizationService.getOrganization(organizationId),
					staleTime: 5 * 60 * 1000,
				}),
				queryClient.prefetchQuery({
					queryKey: organizationKeys.organizationRoles(organizationId),
					queryFn: () =>
						organizationService.getOrganizationRoles(organizationId),
					staleTime: 10 * 60 * 1000,
				}),
				queryClient.prefetchQuery({
					queryKey: organizationKeys.organizationContacts(organizationId),
					queryFn: () =>
						organizationService.getOrganizationContacts(organizationId),
					staleTime: 5 * 60 * 1000,
				}),
			]);
		},
	});
};

// ==================== SUB-CONTRACTORS MUTATIONS ====================

/**
 * Create a new sub-contractor
 * POST /organizations/{organizationId}/sub-contractors
 */
export const useCreateSubContractor = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			data,
		}: {
			organizationId: string;
			data: CreateSubContractorInfo;
		}) => organizationService.createSubContractor(organizationId, data),
		onSuccess: (_result, variables) => {
			const { organizationId } = variables;

			// Invalidate sub-contractors list and count queries
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subContractors(organizationId),
			});
		},
		onError: (error) => {
			console.error("Failed to create sub-contractor:", error);
		},
	});
};

/**
 * Update an existing sub-contractor
 * PUT /organizations/{organizationId}/sub-contractors/{subContractorId}
 */
export const useUpdateSubContractor = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			subContractorId,
			data,
		}: {
			organizationId: string;
			subContractorId: string;
			data: UpdateSubContractorInfo;
		}) =>
			organizationService.updateSubContractor(
				organizationId,
				subContractorId,
				data,
			),
		onSuccess: (_result, variables) => {
			const { organizationId, subContractorId } = variables;

			// Invalidate sub-contractors list and detail queries
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subContractors(organizationId),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subContractorDetail(
					organizationId,
					subContractorId,
				),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subContractorsHistory(
					organizationId,
					subContractorId,
				),
			});
		},
		onError: (error) => {
			console.error("Failed to update sub-contractor:", error);
		},
	});
};

// ==================== SUB-CONTRACTOR SUB-ORGANIZATION ACCESS MUTATIONS ====================

/**
 * Assign a sub-organization to a sub-contractor
 * PUT /organizations/{organizationId}/sub-contractors/{subContractorId}/access/{subOrganizationId}
 */
export const useAssignSubContractorSubOrgAccess = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			subContractorId,
			subOrganizationId,
		}: {
			organizationId: string;
			subContractorId: string;
			subOrganizationId: string;
		}) =>
			organizationService.assignSubContractorSubOrgAccess(
				organizationId,
				subContractorId,
				subOrganizationId,
			),
		onSuccess: (_result, { organizationId, subContractorId }) => {
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subOrganizations(organizationId),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subContractorDetail(
					organizationId,
					subContractorId,
				),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subContractorsHistory(
					organizationId,
					subContractorId,
				),
			});
		},
		onError: (error) => {
			console.error(
				"Failed to assign sub-organization to sub-contractor:",
				error,
			);
		},
	});
};

/**
 * Unassign a sub-organization from a sub-contractor
 * DELETE /organizations/{organizationId}/sub-contractors/{subContractorId}/access/{subOrganizationId}
 */
export const useUnassignSubContractorSubOrgAccess = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			subContractorId,
			subOrganizationId,
		}: {
			organizationId: string;
			subContractorId: string;
			subOrganizationId: string;
		}) =>
			organizationService.unassignSubContractorSubOrgAccess(
				organizationId,
				subContractorId,
				subOrganizationId,
			),
		onSuccess: (_result, { organizationId, subContractorId }) => {
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subOrganizations(organizationId),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subContractorDetail(
					organizationId,
					subContractorId,
				),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.subContractorsHistory(
					organizationId,
					subContractorId,
				),
			});
		},
		onError: (error) => {
			console.error(
				"Failed to unassign sub-organization from sub-contractor:",
				error,
			);
		},
	});
};

// ==================== EMAIL CHECK MUTATIONS ====================

/**
 * Check email usage for an organization
 * GET /organizations/{organizationId}/users/check-email
 * Returns usage information about whether an email is already in use
 */
export const useCheckEmailUsage = () => {
	return useMutation<
		CheckEmailUsage,
		Error,
		{ organizationId: string; email: string }
	>({
		mutationFn: ({ organizationId, email }) =>
			organizationService.checkEmailUsage(organizationId, email),
		onError: (error) => {
			console.error("Failed to check email usage:", error);
		},
	});
};

// ==================== EXPORT MUTATION ====================

/**
 * Export organizations to CSV
 */
export const useExportOrganizations = () => {
	return useMutation({
		mutationFn: async ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params: OrganizationParams;
		}) => {
			const blob = await organizationService.exportOrganizations(
				startIndex,
				count,
				params,
			);
			return blob;
		},
		onError: (error) => {
			console.error("Export failed:", error);
		},
	});
};

// ==================== GROUPED EXPORTS ====================

/**
 * All organization mutations grouped by category
 */
export const organizationMutations = {
	// Core organization mutations
	create: useCreateOrganization,
	update: useUpdateOrganization,
	updateStatus: useUpdateOrganizationStatus,
	optimisticUpdate: useOptimisticUpdateOrganization,
	exportOrganizations: useExportOrganizations,

	// Organization roles mutations
	roles: {
		create: useCreateOrganizationRole,
		update: useUpdateOrganizationRole,
		delete: useDeleteOrganizationRole,
	},

	// Organization contacts mutations
	contacts: {
		update: useUpdateOrganizationContact,
	},

	// Organization OAuth mutations
	oauth: {
		update: useUpdateOrganizationOAuthInfo,
	},

	// Organization users mutations
	users: {
		add: useAddOrganizationUser,
		update: useUpdateOrganizationUser,
		updateStatus: useUpdateOrganizationUserStatus,
		forcePasswordReset: useForceOrganizationUserPasswordReset,
		batchUpdate: useBatchUpdateOrganizationUsers,
		batchUpdateStatuses: useBatchUpdateOrganizationUserStatuses,
		bulkCreate: useBulkCreateOrganizationUsers,
		checkEmail: useCheckEmailUsage,
	},

	// Sub-organizations mutations
	subOrganizations: {
		create: useCreateSubOrganization,
		update: useUpdateSubOrganization,
	},

	// Sub-contractors mutations
	subContractors: {
		create: useCreateSubContractor,
		update: useUpdateSubContractor,
		access: {
			assign: useAssignSubContractorSubOrgAccess,
			unassign: useUnassignSubContractorSubOrgAccess,
		},
	},

	// Organization user data access mutations
	dataAccess: {
		update: useUpdateOrganizationUserDataAccess,
	},

	// Utility mutations
	utils: {
		invalidateQueries: useInvalidateOrganizationQueries,
		prefetch: usePrefetchOrganization,
	},
} as const;
